import {
  Block,
  Branch,
  Role,
  isBranch,
  isCommit,
  isComponentBlock,
  isInstanceBlock,
  isListBlock,
  isNonCrucialBlock,
  isReferenceBlock,
  AtLeast,
  VariantData,
} from '@basehub/data/replicache/index'
import { TreeManager } from '@basehub/utils/tree/index'
import * as React from 'react'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useIsGuestInHisBranch, useUserRole } from '~/hooks-data/use-user-role'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { usePlaygroundCookie } from '~/lib/playground/use-playground-id'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { run } from '@basehub/utils'
import { getVariantsKey } from '@basehub/data/replicache/helpers/variants'

export type EditorMode =
  | 'write:all'
  | 'write:value-and-title'
  | 'write:value-only'
  | 'read'
  | 'preview'

const EditorModeContext = React.createContext<undefined | EditorMode>(undefined)

type EditorModeProviderProps =
  | {
      children?: React.ReactNode
      value?: EditorMode
      parentBlock: Block | null
      block: Block | null
    }
  | {
      value: 'read' | 'preview'
      children?: React.ReactNode
      parentBlock?: never
      block?: never
    }

export const EditorModeProvider = ({
  children,
  value,
  parentBlock = null,
  block = null,
}: EditorModeProviderProps) => {
  const editorMode: EditorMode = value ?? 'write:all'
  const role = useUserRole()
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const editMode = useUserPreferences((s) => s.editMode)
  const selectedBranchOrCommit = useSelectedBranchOrCommitQuery().data
  const isPlayground = !!usePlaygroundCookie()
  const isCommitCheckedOut = Boolean(
    !!selectedBranchOrCommit && isCommit(selectedBranchOrCommit)
  )
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parentBlock ?? undefined,
    block: undefined,
  })

  const resolvedEditorMode = resolveEditorMode(editorMode, {
    parentBlock,
    block,
    role,
    editMode,
    isGuestInHisBranch,
    isCommitCheckedOut,
    branch: Boolean(
      !!selectedBranchOrCommit && isBranch(selectedBranchOrCommit)
    )
      ? (selectedBranchOrCommit as Branch)
      : null,
    treeManager: getChildrenKeys ? { getChildrenKeys } : null,
    isPlayground,
    selectedDashboardVariants,
  })

  return (
    <EditorModeContext.Provider value={resolvedEditorMode.editorMode}>
      {children}
    </EditorModeContext.Provider>
  )
}

export const resolveEditorMode = (
  editorMode: EditorMode,
  {
    role,
    editMode,
    isGuestInHisBranch,
    isCommitCheckedOut,
    branch,
    parentBlock,
    block,
    treeManager,
    isPlayground,
    selectedDashboardVariants,
  }: {
    role: Role
    editMode: boolean
    isGuestInHisBranch: boolean
    isCommitCheckedOut: boolean
    branch: Branch | null
    parentBlock: Block | null
    block: Block | null
    treeManager:
      | AtLeast<TreeManager<Block, NonNullable<unknown>>, 'getChildrenKeys'>
      | undefined
      | null
    isPlayground: boolean
    selectedDashboardVariants: VariantData[]
  }
) => {
  const isValidPlayground =
    isPlayground && branch && (branch.isDefault || !!branch.playgroundId)
  const hasEditRights =
    role !== 'viewer' || isGuestInHisBranch || isValidPlayground
  const canAlterSchema =
    !editMode &&
    (['owner', 'member'].includes(role) ||
      isGuestInHisBranch ||
      isValidPlayground)
  const selectedVariantsKey = getVariantsKey(selectedDashboardVariants ?? [])
  const isReferenced =
    block &&
    parentBlock &&
    isReferenceBlock(parentBlock) &&
    (!treeManager
      ?.getChildrenKeys(idToSourceBlockId(parentBlock))
      .some((cId) => cId === block.id) ||
      // if it is a child, ensure no variant references it other than the selected one
      run(() => {
        /**
         * logic is roughly:
         * - if user is in a variant and the instance is referenced elsewhere, we can't delete it -- just, un-reference it
         * - if user is in default, and instance is referenced elsewhere, same
         */
        const isDefaultVariant = selectedDashboardVariants.length === 0
        const sourceBlockId = idToSourceBlockId(block)
        const defaultTargets =
          parentBlock.value.targetBlockIds.includes(sourceBlockId)
        const someNonSelectedVariantTargets =
          Object.entries(parentBlock.variantOverrides ?? {}).some(
            ([key, { value }]) => {
              if (key === selectedVariantsKey) return false // selected one
              return value.targetBlockIds.includes(sourceBlockId)
            }
          ) ||
          (defaultTargets && !isDefaultVariant)
        return someNonSelectedVariantTargets
      }))

  const isTemplateComponent =
    block && isComponentBlock(block) && parentBlock && isListBlock(parentBlock)

  if (
    !!branch?.archivedAt ||
    editorMode === 'read' ||
    !hasEditRights ||
    (!canAlterSchema && isTemplateComponent) ||
    isCommitCheckedOut ||
    isReferenced
  ) {
    editorMode = 'read'
  } else if (
    editorMode === 'write:value-and-title' ||
    (!canAlterSchema && block && isNonCrucialBlock(block, parentBlock))
  ) {
    editorMode = 'write:value-and-title'
  } else if (
    editorMode === 'write:value-only' ||
    !canAlterSchema ||
    (parentBlock && isInstanceBlock(parentBlock))
  ) {
    editorMode = 'write:value-only'
  } else if (block && isInstanceBlock(block)) {
    if (canAlterSchema) {
      editorMode = 'write:value-and-title'
    } else {
      editorMode = 'write:value-only'
    }
  }

  return { editorMode, isReferenced, isTemplateComponent, canAlterSchema }
}

export const useEditorMode = () => {
  const context = React.useContext(EditorModeContext)
  if (!context) {
    throw new Error('useEditorMode must be used within a EditorModeProvider')
  }
  return context
}
